import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Database, Boxes, GraduationCap, Code2, Globe, MessageSquarePlus } from "lucide-react";
import api, { resolveImageUrl } from "../lib/api";
import Reveal from "../components/Reveal";
import InquiryModal from "../components/InquiryModal";
import { Toaster } from "sonner";

const ICONS = { Sparkles, Database, Boxes, GraduationCap, Code2, Globe };

export default function ProductDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${slug}`).then((r) => setP(r.data)).catch(() => setError(true)).finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="pt-40 text-center text-zinc-500">Loading...</div>;
  if (error || !p) return (
    <div className="pt-40 text-center">
      <p className="text-zinc-400">Product not found.</p>
      <Link to="/products" className="text-[#FF6B1A] mt-4 inline-block">← Back to products</Link>
    </div>
  );

  const Icon = ICONS[p.icon] || Sparkles;
  const accent = p.accent || "#FF6B1A";

  return (
    <div data-testid={`product-detail-${p.slug}`}>
      <Toaster position="top-center" theme="dark" />
      {/* HERO */}
      <section className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          {p.hero_image && <img src={resolveImageUrl(p.hero_image)} alt="" className="w-full h-full object-cover opacity-25" />}
          <div className="absolute inset-0 mesh-bg opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#09090B]/60 to-[#09090B]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <Reveal>
            <Link to="/products" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-[#FF6B1A]">← All products</Link>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-6 inline-flex items-center gap-3">
              <span className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${accent}33, transparent)`, border: `1px solid ${accent}66` }}>
                <Icon size={22} style={{ color: accent }} />
              </span>
              <span className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: accent }}>{p.category}</span>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <h1 className="mt-5 text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-4xl">{p.name}</h1>
          </Reveal>
          <Reveal delay={0.3}>
            <p className="mt-6 text-xl text-zinc-300 max-w-2xl leading-relaxed">{p.tagline}</p>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setInquiryOpen(true)} data-testid="product-hero-inquiry-btn" className="px-8 py-3.5 rounded-full font-semibold text-black inline-flex items-center justify-center gap-2" style={{ background: `linear-gradient(135deg, ${accent}, #FFAA33, #3B82F6)` }}>
                <MessageSquarePlus size={16} /> {p.cta_text || "Request demo"}
              </button>
              {p.demo_url && (
                <a href={p.demo_url} target="_blank" rel="noreferrer" className="px-8 py-3.5 rounded-full border border-white/20 hover:bg-white/5">View live demo</a>
              )}
            </div>
          </Reveal>
        </div>
      </section>

      {/* OVERVIEW + FEATURES */}
      <section className="py-20 bg-[#0b0b0d]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <Reveal>
            <p className="text-zinc-300 text-lg leading-relaxed">{p.description}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-3">
              <p className="text-xs uppercase tracking-[0.25em]" style={{ color: accent }}>Key features</p>
              {(p.features || []).map((f, i) => (
                <div key={`feat-${i}-${f.slice(0, 20)}`} className="flex items-start gap-3 glass rounded-2xl px-5 py-4">
                  <span className="mt-1 w-5 h-5 rounded-full flex items-center justify-center shrink-0" style={{ background: `${accent}22`, border: `1px solid ${accent}66` }}>
                    <Check size={12} style={{ color: accent }} />
                  </span>
                  <span className="text-sm">{f}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* SCREENSHOTS */}
      {p.screenshots && p.screenshots.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <Reveal><h2 className="text-3xl sm:text-4xl font-bold mb-10">A peek <span className="fb-text">inside</span></h2></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {p.screenshots.map((s, i) => (
                <Reveal key={`shot-${s}-${i}`} delay={i * 0.05}>
                  <div className="rounded-3xl overflow-hidden border border-white/10 tilt-card">
                    <img src={resolveImageUrl(s)} alt={`Screenshot ${i + 1}`} className="w-full h-72 object-cover" />
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PRICING */}
      {p.pricing_tiers && p.pricing_tiers.length > 0 && (
        <section className="py-24 bg-[#0b0b0d]">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <Reveal><span className="text-xs font-bold tracking-[0.25em] uppercase" style={{ color: accent }}>Pricing</span></Reveal>
            <Reveal delay={0.1}><h2 className="mt-3 text-4xl font-bold">Choose what fits</h2></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
              {p.pricing_tiers.map((t, i) => (
                <Reveal key={i} delay={i * 0.07}>
                  <div className={`glass rounded-3xl p-7 h-full ${i === 1 ? "border-[#FF6B1A]/50 glow-flame-soft" : ""}`}>
                    {i === 1 && <span className="text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-[#FF6B1A] text-black">Popular</span>}
                    <h3 className="text-xl font-semibold mt-2">{t.name}</h3>
                    <p className="text-3xl font-bold mt-3 fb-text">{t.price}</p>
                    <ul className="mt-5 space-y-2">
                      {(t.items || []).map((it, k) => (
                        <li key={`tier-item-${i}-${k}-${it.slice(0, 12)}`} className="text-sm text-zinc-300 flex items-center gap-2">
                          <Check size={14} className="text-[#FF6B1A]" /> {it}
                        </li>
                      ))}
                    </ul>
                    <Link to={p.cta_link || "/contact"} className={`mt-7 block text-center px-5 py-3 rounded-full font-semibold ${i === 1 ? "bg-gradient-to-r from-[#FF6B1A] to-[#FFAA33] text-black" : "border border-white/15 hover:bg-white/5"}`}
                      onClick={(e) => { e.preventDefault(); setInquiryOpen(true); }}>
                      {p.cta_text || "Get started"}
                    </Link>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <Reveal>
            <h2 className="text-4xl sm:text-5xl font-bold">Ready to try {p.name}?</h2>
            <p className="text-zinc-400 mt-3">Talk to us and our team will set up a tailored demo.</p>
            <Link to={p.cta_link || "/contact"} className="inline-flex items-center gap-2 mt-7 px-8 py-3.5 rounded-full bg-gradient-to-r from-[#FF6B1A] via-[#FFAA33] to-[#3B82F6] text-black font-semibold"
              onClick={(e) => { e.preventDefault(); setInquiryOpen(true); }}>
              <MessageSquarePlus size={16} /> {p.cta_text || "Book demo"}
            </Link>
          </Reveal>
        </div>
      </section>

      <InquiryModal
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        type="product"
        itemKey={p.slug}
        itemLabel={p.name}
        accent={accent}
      />
    </div>
  );
}
